import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AccountSettingsService } from '../account-settings.service';
import { Attachment } from '../../../../../ui-components/lib/upload-files/attachment.interface';
import { FormValidation } from '../../../../../ui-components/enums/form-validation';
import { OptionItem } from '../../../../../ui-components/lib/option.interface';
import { SelectComponent } from '../../../../../ui-components/lib/select/select.component';
import { InputComponent } from '../../../../../ui-components/lib/input/input.component';
import { MobileInputComponent } from '../../../../../ui-components/lib/mobile-input/mobile-input.component';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { UploadFilesComponent } from '../../../../../ui-components/lib/upload-files/upload-files.component';
import { ValidationMessageComponent } from '../../../../../ui-components/lib/validation-message/validation-message.component';
import { FormService } from '../../../../../form-builder/services/form/form.service';
import {
  JobTitle,
  UserProfileDetails,
} from '../../../../common/models/user.model';
import { OverlayLoaderComponent } from '../../../../common/components/overlay-loader/overlay-loader.component';
import { uploadFileUrls } from '../../../../common/constants/upload-file-url';
import { FileUploadService } from '../../../../common/services/file-upload/file-upload.service';
import { regexVariables } from '../../../../common/constants/regex.constant';
import { AppStorage } from '../../../../common/enums/app-storage';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { CookieService } from '../../../../common/services/cookie/cookie.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-edit-profile',
  imports: [
    OverlayLoaderComponent,
    InputComponent,
    ValidationMessageComponent,
    SelectComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    UploadFilesComponent,
    MobileInputComponent,
    RouterLink,
    TenantPathPipe,
  ],
  templateUrl: './edit-profile.component.html',
  styleUrl: './edit-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EditProfileComponent implements OnInit {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;

  fileUploadService = inject(FileUploadService);
  formService = inject(FormService);
  router = inject(Router);
  transloco = inject(TranslocoService);
  toaster = inject(ToasterService);
  accountSettingsService = inject(AccountSettingsService);
  tenantService = inject(TenantService);
  private readonly cookieService = inject(CookieService);
  readonly destroyRef = inject(DestroyRef);

  pageTop = viewChild<ElementRef<HTMLDivElement>>('pageTop');

  profileFormGroup = signal<FormGroup>(new FormGroup({}));
  isLoading = signal<boolean>(true);
  pageTitle = signal<string>(
    this.transloco.translate('edit_profile_screen_title')
  );
  buttonText = signal<string>(
    this.transloco.translate('edit_profile_screen_save_edits_button')
  );
  jobTitles = signal<OptionItem[]>([]);
  profileId = signal<number>(0);
  profileDetails = signal<UserProfileDetails | undefined>(undefined);
  isFormChanged = signal<boolean>(false);
  initialFormValues = signal<any>({});
  uploadFileUrls = uploadFileUrls;
  uploadedImage = signal<Attachment[]>([]);
  selectedCountryISO = signal<string>('');
  selectedPhoneNumber = signal<string>('');

  constructor() {
    this.createForm();
  }

  ngOnInit() {
    this.getUserDetails();
    this.profileFormGroup()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isFormChanged.set(
          this.formService.checkForChanges(
            this.profileFormGroup(),
            this.initialFormValues()
          )
        );
      });
  }

  createForm() {
    this.profileFormGroup.set(
      new FormGroup({
        image: new FormControl(''),
        first_name: new FormControl('', [
          Validators.required,
          Validators.maxLength(FormValidation.maxLength),
        ]),
        last_name: new FormControl('', [
          Validators.required,
          Validators.maxLength(FormValidation.maxLength),
        ]),
        phone: new FormControl('', {
          validators: [
            Validators.required,
            Validators.pattern(regexVariables.phone),
          ],
        }),
        email: new FormControl('', {
          validators: [Validators.required, Validators.email],
        }),
      })
    );

    this.profileFormGroup().addControl(
      'position',
      new FormGroup({
        job_title_id: new FormControl('', Validators.required),
      })
    );
  }

  handleWorkInfoGroupChange(formData: FormData): FormData {
    const workInfoGroup = this.profileFormGroup().get('position') as FormGroup;
    if (workInfoGroup) {
      const workInfoValue = workInfoGroup.value;
      for (const key in workInfoValue) {
        if (workInfoValue[key] !== '' && workInfoValue[key] != null) {
          formData.append(`position[${key}]`, workInfoValue[key]);
        }
      }
    }
    return formData;
  }

  getUserDetails() {
    this.isLoading.set(true);
    this.setProfileId();

    this.accountSettingsService
      .getProfileDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: { data: UserProfileDetails }) => {
          this.profileDetails.set(response.data);
          if (response.data.position?.job_title?.id) {
            this.setJobTitle(response.data.position.job_title);
          }
          this.selectedCountryISO.set(response.data.phone?.country ?? '');
          this.selectedPhoneNumber.set(
            response.data.phone?.normalized_number ?? ''
          );
          this.patchForm(response.data);
          this.isLoading.set(false);
        },
      });
  }

  setProfileId() {
    if (this.cookieService.get(AppStorage.centralUser)) {
      const authData = JSON.parse(
        this.cookieService.get(AppStorage.centralUser) as string
      );
      this.profileId.set(authData.id);
    }
  }

  setJobTitle(jobTitle: JobTitle) {
    this.jobTitles.set([{ id: jobTitle.id, name: jobTitle.name }]);
  }

  patchForm(data: UserProfileDetails) {
    setTimeout(() => {
      this.profileFormGroup().patchValue({
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone?.international_number ?? '',
        email: data.email,
      });

      this.profileFormGroup()
        .get('position')
        ?.patchValue({ job_title_id: data.position?.job_title?.id });

      if (data.image?.url) {
        this.profileFormGroup()
          .get('image')
          ?.setValue(data.image ? [data.image.id] : null);
        this.uploadedImage.set([data.image as Attachment]);
      }

      this.initialFormValues.set(this.profileFormGroup().getRawValue());
      this.isFormChanged.set(
        this.formService.checkForChanges(
          this.profileFormGroup(),
          this.initialFormValues()
        )
      );
      this.isLoading.set(false);
    }, 10);

    this.scrollToTop();
  }

  submit() {
    if (this.profileFormGroup().valid) {
      this.isLoading.set(true);
      const { image, ...formData } = this.profileFormGroup().getRawValue();
      formData.image = image ? image[0] : null;
      this.accountSettingsService
        .editUserProfile(formData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            // Not tied to destroyRef: the navigation below destroys this component.
            this.accountSettingsService.refreshCurrentUser().subscribe();
            this.router.navigate([
              this.tenantService.tenantPath(
                `${EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.PROFILE_DETAILS}`
              ),
            ]);
            this.fileUploadService.file = null;
            this.isLoading.set(false);
          },
          error: error => this.handleError(error),
        });
    }
  }

  goBack() {
    history.back();
  }

  handleError(error: any) {
    this.isLoading.set(false);
    this.toaster.add(error.message, '', '', 'error', true, true);
    this.formService.addControlErrors(error, this.profileFormGroup());
  }

  scrollToTop() {
    (<ElementRef<HTMLDivElement>>this.pageTop()).nativeElement.scrollIntoView({
      behavior: 'smooth',
    });
  }
}
